/* eslint-disable indent */
import { artworkCardTradeFields } from 'api/artworks'
import { getAsks } from 'api/asks'
import { getBids } from 'api/bids'
import { DOCUMENT_STATUS } from 'constants/statuses'
import { useAuth, useCurrencies } from 'contexts'
import qs from 'qs'
import { useQuery } from 'react-query'
import { Ask, Bid } from 'types'
import { pickPrice } from 'utils/currencies'
import { Edition, flatCollectionType } from 'utils/data'
import { getArtworkUrlFromRoot } from 'utils/urls'

type Payload = {
  tradeType: 'ask' | 'bid'
  currentEdition?: Edition | null
  artists?: { id: number; name: string }[]
  name?: string
  id?: number
}

export const useExistingTrade = <T extends null | undefined | Ask | Bid>({
  currentEdition,
  artists,
  name,
  id,
  tradeType,
}: Payload) => {
  const { user } = useAuth()
  const { userCurrency } = useCurrencies()

  const isAsk = tradeType === 'ask'

  const { data: existingTrade } = useQuery(
    [isAsk ? 'existing-ask' : 'existing-bid', user, currentEdition],
    async () => {
      if (!currentEdition || !user) {
        return
      }

      let filters: Record<string, object> = {
        edition: { id: { $eq: currentEdition.id } },
        user: { id: { $eq: user.id } },
        status: { $eq: 'Approved' },
      }

      if (isAsk) {
        filters = {
          ...filters,
          documentStatus: {
            $in: [
              DOCUMENT_STATUS.required,
              DOCUMENT_STATUS.approved,
              DOCUMENT_STATUS.not_required,
              DOCUMENT_STATUS.submitted,
            ],
          },
        }
      }

      const query = qs.stringify(
        {
          filters,
          sort: isAsk
            ? ['price:asc', 'createdAt:desc']
            : ['price:desc', 'createdAt:desc'],
          pagination: { page: 1, pageSize: 1 },
          fields: isAsk
            ? [...artworkCardTradeFields, 'documentStatus']
            : artworkCardTradeFields,
        },
        { encodeValuesOnly: true }
      )

      const { data, error } = isAsk
        ? await getAsks(query)
        : await getBids(query)

      if (error) {
        console.error(error)

        return
      }

      if (!data?.length) return null

      return flatCollectionType(data[0]) as T
    }
  )

  const tradeValue = existingTrade
    ? pickPrice({
        price: existingTrade.price,
        priceCurrency: existingTrade.currency,
        priceGbp: existingTrade.priceConvertedToGbp,
        priceUsd: existingTrade.priceConvertedToUsd,
        priceEur: existingTrade.priceConvertedToEur,
        userCurrency,
      })
    : 0

  return {
    existingTrade,
    editUrl:
      existingTrade && artists && name && id
        ? `${getArtworkUrlFromRoot(
            isAsk ? 'ask-or-sell' : 'bid-or-buy',
            artists,
            name,
            id
          )}/${currentEdition?.name}?action=edit&${isAsk ? 'askId' : 'bidId'}=${
            existingTrade.id
          }&tradeValue=${(tradeValue || 0) / 100}`
        : null,
  }
}
